import React, { useMemo, useState } from 'react';
import { ChevronRight, Zap } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { useI18n } from '../i18n';

// IDs preferidos si el backend los expone (orden de aparición). Si alguno
// no existe, se completa con los primeros que vengan del backend hasta 3.
const PREFERRED_SITUATION_IDS = [
    'servicios_sanitarios',
    'aguas_residuales',
    'mecanismo_quejas',
];

// Avatares deterministas — uno por slot 0/1/2 (no depende del id del backend).
const AVATAR_FALLBACK_URLS = [
    'https://i.pravatar.cc/400?img=12',
    'https://i.pravatar.cc/400?img=47',
    'https://i.pravatar.cc/400?img=33',
];

const pickFirstThreeIds = (allKeys) => {
    const picked = PREFERRED_SITUATION_IDS.filter((id) => allKeys.includes(id));
    if (picked.length >= 3) return picked.slice(0, 3);
    for (const id of allKeys) {
        if (picked.length >= 3) break;
        if (!picked.includes(id)) picked.push(id);
    }
    return picked;
};

const initialsOf = (name = '') =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() || '')
        .join('');

const SituationCard = ({ situation, isSelected, onSelect, accent }) => {
    const [imgError, setImgError] = useState(false);
    const initials = initialsOf(situation.nombre_personaje || '');
    const videoUrl = situation.avatar_video_url || '';
    const avatarUrl = AVATAR_FALLBACK_URLS[situation.index % AVATAR_FALLBACK_URLS.length];

    return (
        <button
            onClick={() => onSelect(situation.id)}
            className={`group relative overflow-hidden rounded-2xl text-left transition-all duration-300 w-full ${
                isSelected ? 'scale-[1.02]' : 'hover:scale-[1.01]'
            }`}
            style={{
                background: 'linear-gradient(180deg, rgba(18, 18, 31, 0.95), rgba(10, 10, 20, 0.95))',
                border: `1px solid ${isSelected ? accent.border : 'rgba(34, 211, 238, 0.18)'}`,
                boxShadow: isSelected
                    ? `0 0 0 1px ${accent.border}, 0 0 24px ${accent.glow}, 0 8px 32px rgba(0,0,0,0.6)`
                    : '0 6px 24px rgba(0,0,0,0.5)',
            }}
        >
            {/* Cabecera con video del personaje en loop muted. `object-contain`
                muestra el video completo respetando su aspect ratio: el
                personaje aparece más pequeño dentro del frame, dejando aire
                arriba/abajo para que el panel de nombre solape solo la zona
                baja sin tapar la cara. */}
            <div
                className="relative h-72 overflow-hidden"
                style={{
                    background:
                        'radial-gradient(ellipse at center, rgba(34, 211, 238, 0.05), rgba(5, 5, 10, 0.95))',
                }}
            >
                {videoUrl ? (
                    <video
                        src={videoUrl}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        className="absolute inset-0 w-full h-full object-contain"
                        style={{
                            objectPosition: 'center top',
                            filter: 'contrast(1.03) saturate(1.05)',
                        }}
                    />
                ) : !imgError ? (
                    <img
                        src={avatarUrl}
                        alt={situation.nombre_personaje}
                        onError={() => setImgError(true)}
                        className="absolute inset-0 w-full h-full object-contain"
                        style={{ objectPosition: 'center top' }}
                    />
                ) : (
                    <div
                        className="absolute inset-0 flex items-center justify-center text-5xl font-mono font-bold"
                        style={{
                            background: `linear-gradient(135deg, ${accent.duo1}, ${accent.duo2})`,
                            color: accent.text,
                            textShadow: `0 0 16px ${accent.glow}`,
                        }}
                    >
                        {initials || '??'}
                    </div>
                )}

                {/* Etiqueta SIT 0X */}
                <div className="absolute top-3 left-3 z-10">
                    <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md font-mono text-[10px] tracking-[0.18em] uppercase"
                        style={{
                            background: 'rgba(5, 5, 10, 0.55)',
                            border: `1px solid ${accent.border}`,
                            color: accent.text,
                            backdropFilter: 'blur(6px)',
                            WebkitBackdropFilter: 'blur(6px)',
                        }}
                    >
                        <Zap className="w-3 h-3" /> SIT {String(situation.index + 1).padStart(2, '0')}
                    </span>
                </div>

                {/* Panel flotante frosted-glass con nombre + cargo, solapado
                    sobre la parte inferior del video (estilo "Eleanor Belle"
                    de la referencia). Alta transparencia + blur fuerte para
                    que se vea el personaje a través de él. */}
                <div
                    className="absolute left-3 right-3 bottom-3 rounded-xl px-4 py-3 z-10"
                    style={{
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        backdropFilter: 'blur(16px) saturate(140%)',
                        WebkitBackdropFilter: 'blur(16px) saturate(140%)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
                    }}
                >
                    <p
                        className="font-mono text-[10px] tracking-[0.2em] uppercase mb-1"
                        style={{ color: accent.text, opacity: 0.95 }}
                    >
                        {situation.tipo_proyecto?.split('–')[0]?.trim() || 'CASO'}
                    </p>
                    <h3 className="text-xl font-bold text-white leading-tight" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>
                        {situation.nombre_personaje}
                    </h3>
                    <p className="text-xs text-white/75 mt-0.5 leading-snug" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                        {situation.cargo_personaje}
                    </p>
                </div>
            </div>

            {/* Cuerpo: descripción + CTA */}
            <div className="p-5 space-y-3">
                <p className="text-sm text-slate-200 leading-relaxed">
                    {situation.descripcion_breve}
                </p>

                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <span
                        className="font-mono text-[10px] tracking-[0.18em] uppercase"
                        style={{ color: accent.text }}
                    >
                        {isSelected ? '> SELECTED' : '> SELECT'}
                    </span>
                    <ChevronRight
                        className="w-4 h-4 transition-transform group-hover:translate-x-1"
                        style={{ color: accent.text }}
                    />
                </div>
            </div>
        </button>
    );
};

const SetupScreen = ({
    scenarios,
    selectedScenario,
    onSelectSituation,
    onStart,
    selectedLanguage,
    onLanguageChange,
}) => {
    const { t } = useI18n();

    const situations = useMemo(() => {
        const all = scenarios || {};
        const allKeys = Object.keys(all);
        const ids = pickFirstThreeIds(allKeys);
        return ids.map((id, index) => ({ id, index, ...all[id] }));
    }, [scenarios]);

    // Paleta neon por tarjeta para evitar monotonía
    const accents = [
        {
            border: 'rgba(34, 211, 238, 0.7)',
            glow: 'rgba(34, 211, 238, 0.35)',
            text: '#67e8f9',
            duo1: 'rgba(34, 211, 238, 0.5)',
            duo2: 'rgba(167, 139, 250, 0.5)',
        },
        {
            border: 'rgba(232, 121, 249, 0.7)',
            glow: 'rgba(232, 121, 249, 0.35)',
            text: '#f0abfc',
            duo1: 'rgba(232, 121, 249, 0.5)',
            duo2: 'rgba(34, 211, 238, 0.5)',
        },
        {
            border: 'rgba(163, 230, 53, 0.7)',
            glow: 'rgba(163, 230, 53, 0.35)',
            text: '#bef264',
            duo1: 'rgba(163, 230, 53, 0.5)',
            duo2: 'rgba(232, 121, 249, 0.45)',
        },
    ];

    return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-10">
            {/* Header arcade */}
            <div className="absolute top-6 right-6">
                <LanguageSelector
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={onLanguageChange}
                />
            </div>

            <div className="w-full max-w-6xl mx-auto">
                <div className="text-center mb-10 scanline-overlay">
                    {/* Brand sutil arriba: identifica el stand desde cierta
                        distancia sin competir con el título principal. */}
                    <p className="font-mono text-[10px] md:text-xs tracking-[0.5em] text-neon-cyan/70 uppercase mb-1">
                        AI Soft Skills Simulation
                    </p>
                    <p
                        className="font-mono text-lg md:text-2xl font-semibold text-gradient uppercase tracking-[0.25em] mb-4"
                        style={{ textShadow: '0 0 16px rgba(34, 211, 238, 0.35)' }}
                    >
                        Negotiation Arena
                    </p>
                    <h1
                        className="text-4xl md:text-5xl font-bold text-gradient flicker mb-3 leading-[1.2] pb-1"
                        style={{ letterSpacing: '0.02em' }}
                    >
                        {t('chooseSituation')}
                    </h1>
                    <p className="text-sm text-slate-400 max-w-xl mx-auto leading-relaxed">
                        {t('chooseSituationDesc')}
                    </p>
                </div>

                {/* Grid de situaciones */}
                <div className="flex flex-wrap justify-center gap-5 mb-10">
                    {situations.map((s, i) => (
                        <div
                            key={s.id}
                            className="w-full sm:w-[340px] md:w-[360px] flex"
                        >
                            <SituationCard
                                situation={s}
                                isSelected={selectedScenario === s.id}
                                onSelect={onSelectSituation}
                                accent={accents[i % accents.length]}
                            />
                        </div>
                    ))}
                </div>

                {/* Botón START */}
                <div className="flex flex-col items-center gap-3">
                    <button
                        onClick={onStart}
                        disabled={!selectedScenario}
                        className={`relative group px-12 py-4 rounded-xl font-mono text-sm tracking-[0.3em] uppercase font-bold transition-all duration-300 ${
                            selectedScenario
                                ? 'text-white scale-100 hover:scale-[1.03]'
                                : 'text-slate-600 cursor-not-allowed'
                        }`}
                        style={
                            selectedScenario
                                ? {
                                      background:
                                          'linear-gradient(135deg, rgba(34, 211, 238, 0.2), rgba(232, 121, 249, 0.2))',
                                      border: '1px solid rgba(232, 121, 249, 0.7)',
                                      boxShadow:
                                          '0 0 16px rgba(232, 121, 249, 0.4), 0 0 32px rgba(34, 211, 238, 0.2), inset 0 0 12px rgba(232, 121, 249, 0.15)',
                                      textShadow: '0 0 12px rgba(232, 121, 249, 0.6)',
                                  }
                                : {
                                      background: 'rgba(18, 18, 31, 0.5)',
                                      border: '1px solid rgba(100, 116, 139, 0.2)',
                                  }
                        }
                    >
                        ▶ {t('startSimulation')}
                    </button>
                    <p
                        className={`font-mono text-[10px] tracking-[0.25em] uppercase ${
                            selectedScenario ? 'text-neon-cyan/60' : 'text-slate-600 blink-slow'
                        }`}
                    >
                        {selectedScenario ? t('readyToStart') : t('selectSituationToStart')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SetupScreen;
